import { CustomState } from "@hardware/Store/Reducers";
import setFieldValue from "@hardware/Store/State/Survey/SetFieldValue";
import { SurveyFormContext } from "@hardware/Widgets/Survey/SurveyForm";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: CustomState) => ({
    workPhoneNumber: state.surveyForm.fieldValues["workPhone"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface WorkPhoneNumberStyles {
    workPhoneNumberTextField?: TextFieldPresentationProps;
}

export const workPhoneNumberStyles: WorkPhoneNumberStyles = {};

const styles = workPhoneNumberStyles;
const phoneRegex = /^([\(\)/\-\.\+\s]*\d\s?(ext)?[\(\)/\-\.\+\s]*){10,}$/;

const WorkPhoneNumber: React.FC<Props> = ({ fields, workPhoneNumber, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const surveyFormContext = React.useContext(SurveyFormContext);
    const [workPhoneNumberErrorMessage, setWorkPhoneNumberErrorMessage] = React.useState("");

    React.useEffect(() => {
        surveyFormContext.validators.workPhoneNumber = validateWorkPhoneNumber;
    }, [isRequired,workPhoneNumber]);

    const validateWorkPhoneNumber = () => {
        const errorMessage =
            !workPhoneNumber && isRequired
                ? (siteMessage("Field_Required", label) as string)
                : !workPhoneNumber || phoneRegex.test(workPhoneNumber)
                ? ""
                : siteMessage("ContactUsForm_PhoneNumberIsInvalidErrorMessage");
        setWorkPhoneNumberErrorMessage(errorMessage as string);
        return !errorMessage;
    };

    const workPhoneNumberChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "workPhone", value: event.target.value });
    };

    const workPhoneNumberBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        validateWorkPhoneNumber();
    };

    return (
        <TextField
            {...styles.workPhoneNumberTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={workPhoneNumber || ""}
            onChange={workPhoneNumberChangeHandler}
            onBlur={workPhoneNumberBlurHandler}
            error={workPhoneNumberErrorMessage}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(WorkPhoneNumber),
    definition: {
        group: "Survey" as any,
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Phone Number",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "(###) ###-####",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
